import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mail, AlertTriangle, CheckCircle } from "lucide-react";

interface DiagnosticResult {
  step: string;
  status: 'success' | 'error' | 'warning';
  message: string;
  details?: any;
}

export const EmailDiagnostics = () => {
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const { toast } = useToast();

  const runDiagnostics = async () => {
    if (!testEmail) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter an email address to test"
      });
      return;
    }

    setTesting(true);
    setResults([]);
    
    const diagnosticResults: DiagnosticResult[] = [];

    try {
      // Step 1: Check if email is already registered
      diagnosticResults.push({
        step: "Email Check",
        status: 'success',
        message: `Testing email delivery for: ${testEmail}`
      });

      // Step 2: Test signup with diagnostic data
      const signUpData = {
        email: testEmail,
        password: 'TempPassword123!',
        options: {
          data: {
            display_name: 'Email Diagnostic Test',
            role: 'vendor',
            diagnostic_test: true,
            test_timestamp: new Date().toISOString()
          }
        }
      };

      console.log('[EmailDiagnostics] Attempting signup with data:', signUpData);

      const { data, error } = await supabase.auth.signUp(signUpData);

      if (error) {
        console.error('[EmailDiagnostics] Signup error:', error);
        
        if (error.message.includes('already registered')) {
          diagnosticResults.push({
            step: "Signup Test",
            status: 'warning',
            message: "Email already registered - testing password reset instead",
            details: error
          });

          // Test password reset if signup fails due to existing user
          const { error: resetError } = await supabase.auth.resetPasswordForEmail(testEmail, {
            redirectTo: `${window.location.origin}/verify?type=recovery`
          });
          
          if (resetError) {
            diagnosticResults.push({
              step: "Password Reset Test",
              status: 'error',
              message: `Password reset failed: ${resetError.message}`,
              details: resetError
            });
          } else {
            diagnosticResults.push({
              step: "Password Reset Test",
              status: 'success',
              message: "Password reset email requested successfully"
            });
          }
        } else {
          diagnosticResults.push({
            step: "Signup Test",
            status: 'error',
            message: `Signup failed: ${error.message}`,
            details: error
          });
        }
      } else {
        console.log('[EmailDiagnostics] Signup successful:', data);
        
        diagnosticResults.push({
          step: "Signup Test",
          status: 'success',
          message: `Signup successful. User ID: ${data.user?.id}`,
          details: {
            userId: data.user?.id,
            emailConfirmed: data.user?.email_confirmed_at,
            userMetadata: data.user?.user_metadata
          }
        });

        if (data.user && !data.user.email_confirmed_at) {
          diagnosticResults.push({
            step: "Email Confirmation Status",
            status: 'warning',
            message: "User created but email not confirmed - confirmation email should have been sent"
          });
        } else if (data.user?.email_confirmed_at) {
          diagnosticResults.push({
            step: "Email Confirmation Status",
            status: 'success',
            message: "Email already confirmed"
          });
        }
      }

      // Step 3: Check recent auth logs for this email
      setTimeout(async () => {
        try {
          const { data: authLogs } = await supabase.rpc('get_admin_setting', { 
            setting_path: 'debug.auth_logs' 
          });
          
          diagnosticResults.push({
            step: "Auth Logs Check",
            status: 'success',
            message: "Auth logs checked (if available)",
            details: authLogs
          });
        } catch (logError) {
          console.log('[EmailDiagnostics] Could not fetch auth logs:', logError);
          diagnosticResults.push({
            step: "Auth Logs Check",
            status: 'warning',
            message: "Could not access auth logs - this is normal for security reasons"
          });
        }
        
        setResults([...diagnosticResults]);
      }, 1000);

    } catch (error: any) {
      console.error('[EmailDiagnostics] Diagnostic error:', error);
      diagnosticResults.push({
        step: "General Error",
        status: 'error',
        message: `Diagnostic failed: ${error.message}`,
        details: error
      });
    }

    setResults(diagnosticResults);
    setTesting(false);

    toast({
      title: "Email Diagnostics Complete",
      description: "Check the results below for details"
    });
  };

  const getStatusIcon = (status: DiagnosticResult['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
    }
  };

  const getStatusColor = (status: DiagnosticResult['status']) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Delivery Diagnostics
        </CardTitle>
        <CardDescription>
          Test signup email delivery and diagnose any issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter test email address"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            disabled={testing}
          />
          <Button 
            onClick={runDiagnostics} 
            disabled={testing || !testEmail}
          >
            {testing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Testing...
              </>
            ) : (
              'Run Diagnostics'
            )}
          </Button>
        </div>

        {results.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold">Diagnostic Results:</h3>
            {results.map((result, index) => (
              <div key={index} className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(result.status)}
                    <span className="font-medium">{result.step}</span>
                  </div>
                  <Badge className={getStatusColor(result.status)}>
                    {result.status}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 mb-2">{result.message}</p>
                {result.details && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-500">View Details</summary>
                    <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto">
                      {JSON.stringify(result.details, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="text-xs text-gray-500 space-y-1">
          <p><strong>Note:</strong> This diagnostic will:</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Attempt to create a test account with the provided email</li>
            <li>Check if confirmation emails are sent</li>
            <li>Test password reset emails if signup fails due to existing account</li>
            <li>Log all attempts for debugging purposes</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};